import type { CSSProperties } from "react";
import type { GameResult, PlayerId } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import { MahjongTile } from "./tiles";

export interface EndPanelProps {
  result: GameResult;
  you: PlayerId;
  nameFor: (playerId: PlayerId) => string;
  onRestart?: () => void;
  onNewGame?: () => void;
  restartLabel?: string;
  newGameLabel?: string;
}

export function EndPanel({
  result,
  you,
  nameFor,
  onRestart,
  onNewGame,
  restartLabel,
  newGameLabel,
}: EndPanelProps) {
  const { t, tile, meld } = useI18n();
  const isWin = result.type === "win";
  const youWon = isWin && result.winner === you;

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={t("end.dialog")}>
      <div className="overlay__card panel">
        <h2>{isWin ? (youWon ? t("end.win.you") : t("end.win.other")) : t("end.draw.title")}</h2>

        {isWin ? (
          <>
            <p>
              {result.by === "self-draw"
                ? t("end.win.selfDraw", {
                    player: nameFor(result.winner),
                    tile: tile(result.winningTile),
                  })
                : t("end.win.fromDiscard", {
                    player: nameFor(result.winner),
                    tile: tile(result.winningTile),
                    from: nameFor(result.from ?? result.winner),
                  })}
            </p>
            <div className="win-hand">
              {/*
                * The sets are revealed one after another rather than all at
                * once: it is the closest a table gets to celebrating, and it
                * reads the hand out in the order a player would lay it down.
                */}
              {result.hand.melds.map((group, index) => (
                <div
                  className="win-hand__meld"
                  key={index}
                  style={{ "--meld-index": index } as CSSProperties}
                >
                  <span className="win-hand__label">{meld(group.kind)}</span>
                  <span className="win-hand__tiles">
                    {group.tiles.map((kind, position) => (
                      <MahjongTile key={position} kind={kind} size="sm" />
                    ))}
                  </span>
                </div>
              ))}
              <div
                className="win-hand__meld"
                style={{ "--meld-index": result.hand.melds.length } as CSSProperties}
              >
                <span className="win-hand__label">{meld("pair")}</span>
                <span className="win-hand__tiles">
                  <MahjongTile kind={result.hand.pair} size="sm" />
                  <MahjongTile kind={result.hand.pair} size="sm" />
                </span>
              </div>
            </div>
            <p className="overlay__note">{t("end.noScore")}</p>
          </>
        ) : (
          <p>{t("end.draw.body")}</p>
        )}

        <div className="overlay__actions">
          {onNewGame ? (
            <button type="button" className="button button--primary" onClick={onNewGame}>
              {newGameLabel ?? t("end.newGame")}
            </button>
          ) : null}
          {onRestart ? (
            <button type="button" className="button" onClick={onRestart}>
              {restartLabel ?? t("end.restart")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
